/**
 * useUpgradeDowngradeSubscription Hook
 * Custom React Query hook for upgrading or downgrading a subscription
 */

import {useMutation, useQueryClient} from 'react-query';
import {useUserStore} from '~providers/userStore/zustand';
import {BASE_API_URL} from '@env';

interface UpgradeDowngradePayload {
  new_plan_id: string;
}

interface UpgradeDowngradeResponse {
  message: string;
  subscription?: any;
}

/**
 * Upgrade or downgrade subscription via API
 */
async function upgradeDowngradeSubscription(
  token: string, 
  payload: UpgradeDowngradePayload
): Promise<UpgradeDowngradeResponse> {
  const response = await fetch(`${BASE_API_URL}stripe/subscription/upgrade-downgrade`, {
    method: 'PUT',
    headers: {
      'accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to upgrade/downgrade subscription: ${response.status}`);
  }

  return response.json();
}

export const useUpgradeDowngradeSubscription = () => {
  const queryClient = useQueryClient();
  const token = useUserStore.getState().token;

  return useMutation(
    (payload: UpgradeDowngradePayload) => upgradeDowngradeSubscription(token!, payload),
    {
      onSuccess: () => {
        // Invalidate active plan query to refetch the new status
        queryClient.invalidateQueries(['activePlan']);
      },
    }
  );
};
